<script setup lang="ts">
type ActiveButton = "review-and-confirm" | "checkout" | "select-recipient";

const route = useRoute();
const localePath = useLocalePath();
const client = useClient();

const { t } = useI18n();
const { isLoggedIn } = useAuth();
const globalStore = useGlobalStore();
const { isLoading, progress, isClientBlocked } = storeToRefs(globalStore);
const { payGiftCard } = useGiftCard();
const { navLoading, runWithLoading } = useFooterNav();

const {
  selectedLocation,
  selectedPaymentMethod,
  tapLoading,
  isNavigating,
  isCardValid,
  payGiftCardLoading,
  giftCardSelected,
  goToGiftCardSummary,
  currentStep,
  recipientFormRef,
  goToGiftCardCheckout,
  giftCardSummary,
  invoice,
} = storeToRefs(useBusinessStore());

const activeButton = computed((): ActiveButton | undefined => {
  const keys = (route.meta.keys as string[]) ?? [];
  const routeName = String(route.name ?? "");
  const pathSegments = route.path.split("/").filter(Boolean);
  const onGiftCardsIndex =
    pathSegments[pathSegments.length - 1] === "gift-cards";

  if (keys.includes("checkout") || route.path.includes("/gift-cards/checkout"))
    return "checkout";

  if (
    keys.includes("review-and-confirm") ||
    routeName.includes("review-and-confirm") ||
    route.path.includes("/gift-cards/review-and-confirm")
  )
    return "review-and-confirm";

  if (
    keys.includes("choose-amount") ||
    keys.includes("select-recipient") ||
    (onGiftCardsIndex &&
      keys.includes("gift-cards") &&
      giftCardSelected.value)
  )
    return "select-recipient";

  return undefined;
});

const displayTotal = computed(() => {
  const summaryTotal = giftCardSummary.value?.grand_total;
  const isSummaryStep =
    activeButton.value === "review-and-confirm" ||
    activeButton.value === "checkout";

  if (isSummaryStep && summaryTotal !== undefined && summaryTotal !== null) {
    return useFormatAmount(summaryTotal);
  }

  return useFormatAmount(giftCardSelected.value?.price);
});

const completeProfileToContinue = async () => {
  goToGiftCardSummary.value = true;
  await navigateTo(localePath(`/complete`));
};

const loginToContinue = () => {
  goToGiftCardSummary.value = true;
  globalStore.openSignInModal({
    onSuccess: (c) => {
      if (c?.profile_completed && c?.mobile) {
        runWithLoading(handleConfirm);
      }
    },
  });
};

const submitPayment = async () => {
  if (selectedPaymentMethod.value?.key === "tap-cc") {
    const windowCardSDK = ref(window.CardSDK);
    const { tokenize } = windowCardSDK.value;
    tapLoading.value = true;
    await tokenize();
    return;
  }

  await payGiftCard();
};

const navigateToReviewAndConfirm = async () => {
  if (currentStep.value === "select_recipient") {
    if (giftCardSelected.value?.recipient?.id === "gift") {
      const form = recipientFormRef.value;

      if (!form) return;

      const { errors } = await form.validate();
      if (errors) {
        console.log(errors);
        return;
      }
    }

    await navigateTo(
      localePath(
        `/${selectedLocation.value?.identifier}/gift-cards/review-and-confirm`
      )
    );
  } else {
    currentStep.value = "select_recipient";
  }
};

const handleConfirm = async () => {
  goToGiftCardCheckout.value = true;
  await navigateTo(
    localePath(`/${selectedLocation.value?.identifier}/gift-cards/checkout`)
  );
};
</script>

<template>
  <CommonFooterShell
    :label1="t('page.gift_card.expiration')"
    :value1="giftCardSelected?.expiration"
    :label2="t('total')"
    :value2="$formatPrice(displayTotal)"
  >
    <template #action>
      <UButton
        v-if="activeButton === 'select-recipient'"
        :label="t('label.continue')"
        :color="`${selectedLocation?.theme_name}-secondary`"
        :loading="navLoading"
        :disabled="
          !giftCardSelected?.recipient?.id && currentStep === 'select_recipient'
        "
        size="xl"
        class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
        @click="runWithLoading(navigateToReviewAndConfirm)"
      />

      <template v-else-if="activeButton === 'review-and-confirm'">
        <template v-if="isLoggedIn">
          <UButton
            v-if="client?.profile_completed"
            :label="t('label.confirm')"
            :color="`${selectedLocation?.theme_name}-secondary`"
            :loading="navLoading"
            :disabled="isLoading || progress || isClientBlocked"
            size="xl"
            class="min-w-26 xs:min-w-28 rounded-xl py-3 text-center flex items-center justify-center"
            @click="runWithLoading(handleConfirm)"
          />
          <UButton
            v-else
            :disabled="isLoading || progress"
            :loading="navLoading"
            :label="$t('label.complete_profile')"
            :color="`${selectedLocation?.theme_name}-secondary`"
            size="xl"
            class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center truncate"
            @click="runWithLoading(completeProfileToContinue)"
          />
        </template>
        <UButton
          v-else
          :label="$t('label.sign_in')"
          :disabled="isLoading || progress"
          :loading="navLoading"
          :color="`${selectedLocation?.theme_name}-secondary`"
          size="xl"
          class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
          @click="runWithLoading(loginToContinue)"
        />
      </template>

      <UButton
        v-else-if="activeButton === 'checkout'"
        :label="$t('label.checkout')"
        :loading="payGiftCardLoading || tapLoading"
        :color="`${selectedLocation?.theme_name}-secondary`"
        :disabled="
          !selectedPaymentMethod?.id ||
          (!isCardValid && selectedPaymentMethod?.key === 'tap-cc') ||
          (!!selectedPaymentMethod?.accept_invoice && !invoice) ||
          isNavigating
        "
        size="xl"
        class="min-w-26 xs:min-w-28 rounded-xl py-3 flex items-center justify-center"
        @click="submitPayment"
      />
    </template>
  </CommonFooterShell>
</template>
